import { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { orderAPI, publicAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  CreditCard, 
  Smartphone, 
  Link2, 
  Zap, 
  QrCode, 
  Shield, 
  Clock,
  ChevronRight,
  Menu,
  X,
  Check
} from 'lucide-react';
import MeelinkLifetimePromo from '@/components/MeelinkLifetimePromo';

// Product images
const PRODUCT_IMAGES = {
  hero: 'https://customer-assets.emergentagent.com/job_smarttag/artifacts/opf35c7d_012%20%284%29.jpg',
  lifestyle: 'https://customer-assets.emergentagent.com/job_smarttag/artifacts/87f86xba_PHOTO-2025-12-26-15-39-41.jpg',
};

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass py-3' : 'py-5'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">MeTag</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-slate-300 hover:text-white transition-colors">Features</a>
            <a href="#how-it-works" className="text-slate-300 hover:text-white transition-colors">How It Works</a>
            <a href="#pricing" className="text-slate-300 hover:text-white transition-colors">Pricing</a>
            
            <div className="sale-badge px-4 py-1.5 rounded-full text-white text-sm font-bold">
              69% OFF
            </div>

            {isAuthenticated ? (
              <Button onClick={() => navigate('/dashboard')} className="btn-primary">
                Dashboard
              </Button>
            ) : (
              <Button onClick={() => navigate('/login')} className="btn-primary">
                Login
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            className="md:hidden mt-4 pb-4 space-y-4"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
          >
            <a href="#features" className="block text-slate-300 hover:text-white py-2">Features</a>
            <a href="#how-it-works" className="block text-slate-300 hover:text-white py-2">How It Works</a>
            <a href="#pricing" className="block text-slate-300 hover:text-white py-2">Pricing</a>
            {isAuthenticated ? (
              <Button onClick={() => navigate('/dashboard')} className="w-full btn-primary">
                Dashboard
              </Button>
            ) : (
              <Button onClick={() => navigate('/login')} className="w-full btn-primary">
                Login
              </Button>
            )}
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = ({ onBuyNow }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);

  return (
    <section className="min-h-screen pt-24 pb-16 relative overflow-hidden hero-pattern" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div 
            className="space-y-8"
            initial="initial"
            animate="animate"
            variants={stagger}
          >
            <motion.div variants={fadeUp} className="inline-block">
              <div className="sale-badge px-6 py-2 rounded-full text-white font-bold text-sm inline-flex items-center gap-2">
                <Zap className="w-4 h-4" />
                LIMITED TIME: 69% OFF
              </div>
            </motion.div>

            <motion.h1 
              variants={fadeUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight"
            >
              NFC Smart Card for{' '}
              <span className="text-gradient">Digital Identity</span>
            </motion.h1>

            <motion.p 
              variants={fadeUp}
              className="text-lg text-slate-400 max-w-lg leading-relaxed"
            >
              Share your digital profile with a single tap. No app required. 
              Update your links anytime. The future of networking is here.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <Button 
                onClick={onBuyNow}
                className="btn-primary text-lg px-8 py-4 flex items-center gap-2"
                data-testid="hero-buy-now-btn"
              >
                Buy Now - ₹310
                <ChevronRight className="w-5 h-5" />
              </Button>
              <Button 
                variant="outline"
                className="btn-secondary text-lg px-8 py-4"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </motion.div>

            <motion.div variants={fadeUp} className="flex items-center gap-6 pt-4">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-10 h-10 rounded-full bg-slate-700 border-2 border-slate-900" />
                ))}
              </div>
              <div className="text-sm text-slate-400">
                <span className="text-white font-semibold">2,000+</span> professionals already using MeTag
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Product Image */}
          <motion.div 
            className="relative"
            style={{ y }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              {/* Glow effect */}
              <div className="absolute inset-0 bg-violet-500/20 blur-3xl rounded-full" />
              
              {/* Card Image */}
              <div className="relative product-image rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={PRODUCT_IMAGES.hero} 
                  alt="MeTag NFC Card"
                  className="w-full h-auto"
                />
              </div>

              {/* Floating badge */}
              <motion.div
                className="absolute -bottom-4 -right-4 glass px-4 py-3 rounded-2xl"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Check className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">NFC + QR</p>
                    <p className="text-sm text-white font-semibold">Dual Access</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Features Section - Bento Grid
const FeaturesSection = () => {
  const features = [
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: 'NFC Enabled',
      description: 'Tap on any NFC-enabled smartphone to instantly share your profile.',
      gradient: 'from-violet-500 to-purple-600',
      large: true
    },
    {
      icon: <QrCode className="w-8 h-8" />,
      title: 'QR Fallback',
      description: 'Printed QR code for universal compatibility.',
      gradient: 'from-emerald-500 to-teal-600'
    },
    {
      icon: <Link2 className="w-8 h-8" />,
      title: 'Dynamic Links',
      description: 'Update your linked URL anytime from dashboard.',
      gradient: 'from-orange-500 to-red-600'
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: 'No App Required',
      description: 'Works directly with browser. No downloads.',
      gradient: 'from-blue-500 to-cyan-600'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Secure',
      description: 'Your data is encrypted and protected.',
      gradient: 'from-pink-500 to-rose-600'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Instant Setup',
      description: 'Get started in under 2 minutes.',
      gradient: 'from-amber-500 to-yellow-600'
    }
  ];

  return (
    <section id="features" className="py-24 relative" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-violet-400 font-mono text-sm uppercase tracking-widest">Features</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4">
            Everything You Need
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className={`group relative overflow-hidden rounded-3xl bg-slate-900/50 border border-white/5 p-8 hover:border-violet-500/50 transition-all duration-500 card-hover ${feature.large ? 'md:col-span-2 lg:col-span-1' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-white mb-6`}>
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
              <p className="text-slate-400">{feature.description}</p>
              
              {/* Hover glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-5`} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      number: '01',
      title: 'Buy MeTag Card',
      description: 'Order your premium NFC card with 69% discount.',
      icon: <CreditCard className="w-6 h-6" />
    },
    {
      number: '02',
      title: 'Tap or Scan',
      description: 'Use NFC tap on iPhone or scan the QR code.',
      icon: <Smartphone className="w-6 h-6" />
    },
    {
      number: '03',
      title: 'Link Your Profile',
      description: 'Connect any URL - portfolio, LinkedIn, bio link.',
      icon: <Link2 className="w-6 h-6" />
    }
  ];

  return (
    <section id="how-it-works" className="py-24 bg-slate-900/30" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-violet-400 font-mono text-sm uppercase tracking-widest">How It Works</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4">
            Three Simple Steps
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              className="relative"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
            >
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-12 left-1/2 w-full h-0.5 bg-gradient-to-r from-violet-500/50 to-transparent" />
              )}

              <div className="glass rounded-3xl p-8 text-center relative z-10">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-violet-500/20 text-violet-400 mb-6">
                  {step.icon}
                </div>
                <span className="text-5xl font-black text-violet-500/30 absolute top-4 right-6">
                  {step.number}
                </span>
                <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                <p className="text-slate-400">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = ({ onBuyNow }) => {
  return (
    <section id="pricing" className="py-24" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-violet-400 font-mono text-sm uppercase tracking-widest">Pricing</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4">
            One Card, Unlimited Possibilities
          </h2>
        </motion.div>

        <motion.div
          className="max-w-lg mx-auto"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
        >
          <div className="relative">
            {/* Sale ribbon */}
            <div className="absolute -top-4 -right-4 sale-badge px-4 py-2 rounded-full text-white font-bold text-sm z-10">
              69% OFF
            </div>

            <div className="glass rounded-3xl p-8 border border-violet-500/30">
              <div className="flex items-center gap-4 mb-6">
                <img 
                  src={PRODUCT_IMAGES.lifestyle}
                  alt="MeTag Card"
                  className="w-24 h-24 rounded-2xl object-cover"
                />
                <div>
                  <h3 className="text-2xl font-bold text-white">MeTag NFC Card</h3>
                  <p className="text-slate-400">Premium Smart Card</p>
                </div>
              </div>

              <div className="flex items-baseline gap-4 mb-8">
                <span className="text-5xl font-black text-white">₹310</span>
                <span className="text-xl text-slate-500 line-through">₹999</span>
                <span className="text-emerald-400 font-semibold">Save ₹689</span>
              </div>

              <ul className="space-y-4 mb-8">
                {[
                  'Premium NFC chip',
                  'QR code fallback',
                  'Lifetime updates',
                  'Free shipping',
                  'Dashboard access'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300">
                    <Check className="w-5 h-5 text-emerald-400" />
                    {item}
                  </li>
                ))}
              </ul>

              <Button 
                onClick={onBuyNow}
                className="w-full btn-primary text-lg py-4"
                data-testid="pricing-buy-now-btn"
              >
                Buy Now
              </Button>

              <p className="text-center text-slate-500 text-sm mt-4">
                Ships within 1-3 business days
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Product Gallery Section
const ProductGallery = () => {
  return (
    <section className="py-24 bg-slate-900/30" data-testid="product-gallery">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-violet-400 font-mono text-sm uppercase tracking-widest">Product</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4">
            Premium Quality Card
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          <motion.div
            className="product-image rounded-3xl overflow-hidden aspect-square"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img 
              src={PRODUCT_IMAGES.hero}
              alt="MeTag NFC Card Front"
              className="w-full h-full object-cover"
            />
          </motion.div>

          <motion.div
            className="product-image rounded-3xl overflow-hidden aspect-square"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img 
              src={PRODUCT_IMAGES.lifestyle}
              alt="MeTag NFC Card Lifestyle"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="py-16 border-t border-slate-800" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white">MeTag</span>
            </Link>
            <p className="text-slate-400 mb-4 max-w-sm">
              NFC Smart Card for Digital Identity. Share your profile with a single tap.
            </p>
            <p className="text-slate-500 text-sm">
              Powered by CogSight
            </p>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">Legal</h4>
            <ul className="space-y-2">
              <li><Link to="/privacy-policy" className="text-slate-400 hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms-conditions" className="text-slate-400 hover:text-white transition-colors">Terms & Conditions</Link></li>
              <li><Link to="/return-policy" className="text-slate-400 hover:text-white transition-colors">Refund Policy</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-2 text-slate-400">
              <li>+91 78376 40500</li>
              <li>support@metag.com</li>
              <li>Rajiv Enclave, SBS Nagar</li>
              <li>Ludhiana, Pb. India - 141001</li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} MeTag.cc. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

// Address Modal Component
const AddressModal = ({ isOpen, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay flex items-center justify-center p-4" data-testid="address-modal">
      <motion.div
        className="glass rounded-3xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Shipping Address</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Full Name</label>
            <Input
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              required
              className="input-field"
              placeholder="John Doe"
              data-testid="address-fullname"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Email</label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="input-field"
                placeholder="john@example.com"
                data-testid="address-email"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Phone</label>
              <Input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="input-field"
                placeholder="+91 98765 43210"
                data-testid="address-phone"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Address</label>
            <Input
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              className="input-field"
              placeholder="123 Main Street"
              data-testid="address-street"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">City</label>
              <Input
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                className="input-field"
                placeholder="Ludhiana"
                data-testid="address-city"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">State</label>
              <Input
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
                className="input-field"
                placeholder="Punjab"
                data-testid="address-state"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Pincode</label>
              <Input
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                required
                className="input-field"
                placeholder="141001"
                data-testid="address-pincode"
              />
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full btn-primary mt-6"
            disabled={loading}
            data-testid="address-submit-btn"
          >
            {loading ? 'Processing...' : 'Proceed to Payment - ₹310'}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

// Main Landing Page Component
export default function LandingPage() {
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleBuyNow = () => {
    if (!isAuthenticated) {
      toast.info('Please login to purchase');
      navigate('/login');
      return;
    }
    setShowAddressModal(true);
  };

  const handleAddressSubmit = async (address) => {
    setLoading(true);
    try {
      // Get Razorpay key
      const keyResponse = await publicAPI.getRazorpayKey();
      const razorpayKey = keyResponse.data.keyId;

      // Create order
      const orderResponse = await orderAPI.create({
        address,
        productName: 'MeTag NFC Card',
        originalPrice: 999,
        discountPrice: 310
      });

      const { razorpayOrderId, amount } = orderResponse.data;

      // Open Razorpay checkout
      const options = {
        key: razorpayKey,
        amount: amount,
        currency: 'INR',
        name: 'MeTag',
        description: 'MeTag NFC Card',
        order_id: razorpayOrderId,
        handler: async (response) => {
          try {
            await orderAPI.verify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            toast.success('Payment successful! Check your dashboard.');
            setShowAddressModal(false);
            navigate('/dashboard/orders');
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: address.fullName,
          email: address.email,
          contact: address.phone
        },
        theme: {
          color: '#7c3aed'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617]">
      <Navbar />
      <HeroSection onBuyNow={handleBuyNow} />
      <FeaturesSection />
      <HowItWorksSection />
      <ProductGallery />
      <PricingSection onBuyNow={handleBuyNow} />
      <MeelinkLifetimePromo variant="section" />
      <Footer />

      <AddressModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSubmit={handleAddressSubmit}
        loading={loading}
      />

      {/* Razorpay Script */}
      <script src="https://checkout.razorpay.com/v1/checkout.js" />
    </div>
  );
}
